'use client';

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  Award,
  BookOpen,
  ClipboardCheck,
  FileText,
  GraduationCap,
  PlayCircle,
  Terminal,
} from "lucide-react";
import { cn } from "../../utils/cn";

/**
 * Card — base surface + MV5 learning-item variants.
 *
 * Base composition mirrors shadcn: Card + CardHeader + CardTitle + CardBody
 * + CardFooter. The `variant` prop on the root Card sets the accent stripe,
 * the default icon slot, and the aria role for the item type.
 *
 * Variants: `default` (plain surface) plus 6 learning-item flavours
 *   - lecture (video)
 *   - lab (terminal)
 *   - quiz (assessment)
 *   - reader (long-form)
 *   - course (path node)
 *   - cert (credential)
 */

const cardVariants = cva(
  [
    "flex flex-col overflow-hidden",
    "bg-chrome-surface text-ink-primary",
    "border border-chrome-border rounded-card",
    "transition-colors duration-standard ease-standard",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "",
        lecture: "border-l-4 border-l-accent-violet",
        lab: "border-l-4 border-l-status-success",
        quiz: "border-l-4 border-l-status-warn",
        reader: "border-l-4 border-l-ink-muted",
        course: "border-l-4 border-l-accent-violet-hover",
        cert:
          "border-l-4 border-l-accent-violet bg-chrome-panel",
      },
      interactive: {
        true: "cursor-pointer hover:border-accent-violet hover:shadow-sm focus-within:border-accent-violet",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      interactive: false,
    },
  },
);

const variantIcon: Record<
  NonNullable<CardProps["variant"]>,
  React.ComponentType<{ className?: string }>
> = {
  default: FileText,
  lecture: PlayCircle,
  lab: Terminal,
  quiz: ClipboardCheck,
  reader: BookOpen,
  course: GraduationCap,
  cert: Award,
};

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, variant = "default", interactive, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-slot="card"
      data-variant={variant}
      className={cn(cardVariants({ variant, interactive, className }))}
      {...props}
    />
  );
});
Card.displayName = "Card";

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CardHeader({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="card-header"
      className={cn(
        "flex items-start gap-space-12 px-space-20 pt-space-20 pb-space-12",
        className,
      )}
      {...props}
    />
  );
});
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(function CardTitle({ className, ...props }, ref) {
  return (
    <h3
      ref={ref}
      data-slot="card-title"
      className={cn("text-heading-22 text-ink-primary", className)}
      {...props}
    />
  );
});
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return (
    <p
      ref={ref}
      data-slot="card-description"
      className={cn("text-body-14 text-ink-muted", className)}
      {...props}
    />
  );
});
CardDescription.displayName = "CardDescription";

export const CardBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CardBody({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="card-body"
      className={cn("px-space-20 pb-space-16 text-body-14 text-ink-primary", className)}
      {...props}
    />
  );
});
CardBody.displayName = "CardBody";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CardFooter({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="card-footer"
      className={cn(
        "mt-auto flex items-center justify-between gap-space-8 px-space-20 py-space-16 border-t border-chrome-border",
        className,
      )}
      {...props}
    />
  );
});
CardFooter.displayName = "CardFooter";

/**
 * CardTypeIcon — renders the default lucide icon for a card variant.
 * Callers use this in CardHeader for a consistent visual anchor without
 * having to import lucide separately.
 */
export function CardTypeIcon({
  variant = "default",
  className,
}: {
  variant?: CardProps["variant"];
  className?: string;
}) {
  const Icon = variantIcon[variant ?? "default"];
  return (
    <span
      data-slot="card-type-icon"
      className={cn(
        "inline-flex items-center justify-center size-8 rounded-chip bg-chrome-panel text-accent-violet shrink-0",
        className,
      )}
    >
      <Icon className="size-4" />
    </span>
  );
}

export { cardVariants };
